import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { api } from "../lib/api";
import { formatCategoryName } from "../lib/site";
import ProductCard from "../components/ProductCard";
import EditorialMarquee from "../components/EditorialMarquee";
import { MaskedLines, FadeIn, Reveal } from "../components/motion/Reveal";
import PageMeta from "../components/PageMeta";

const PRODS_CACHE_KEY = "pbg_all_products";
const CATS_CACHE_KEY = "pbg_home_categories";

const getCachedArray = (key) => {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(key);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch (_) {}
  return [];
};

const Products = () => {
  const [products, setProducts] = useState(() => getCachedArray(PRODS_CACHE_KEY));
  const [apiCategories, setApiCategories] = useState(() => getCachedArray(CATS_CACHE_KEY));
  const [loading, setLoading] = useState(() => getCachedArray(PRODS_CACHE_KEY).length === 0);
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    api.get("/categories")
      .then((r) => {
        if (Array.isArray(r.data)) {
          setApiCategories(r.data);
          try { localStorage.setItem(CATS_CACHE_KEY, JSON.stringify(r.data)); } catch (_) {}
        }
      })
      .catch(() => { });

    api.get("/products")
      .then((r) => {
        if (Array.isArray(r.data)) {
          setProducts(r.data);
          try { localStorage.setItem(PRODS_CACHE_KEY, JSON.stringify(r.data)); } catch (_) {}
        }
      })
      .catch(() => { })
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => {
    const list = ["All"];
    const seen = new Set(["all"]);

    // 1. Dynamic parent categories only from /api/categories (parent_id is null, 0, or "")
    if (Array.isArray(apiCategories) && apiCategories.length > 0) {
      const isParent = (c) => !c.parent_id || c.parent_id === 0 || c.parent_id === "0" || c.parent_id === "null";

      const parents = apiCategories
        .filter(isParent)
        .sort((a, b) => (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0) || Number(a.id) - Number(b.id));

      parents.forEach((c) => {
        const rawName = typeof c === "string" ? c : c?.name;
        if (rawName) {
          const formatted = formatCategoryName(rawName);
          const lower = formatted.toLowerCase();
          if (!seen.has(lower)) {
            seen.add(lower);
            list.push(formatted);
          }
        }
      });
    } else {
      // Fallback: extract parent categories from products
      products.forEach((p) => {
        const cat = p.parent_category || p.category;
        if (cat) {
          const formatted = formatCategoryName(cat);
          const lower = formatted.toLowerCase();
          if (!seen.has(lower)) {
            seen.add(lower);
            list.push(formatted);
          }
        }
      });
    }

    return list;
  }, [apiCategories, products]);

  const rawParam = params.get("category");
  const activeCategory = useMemo(() => {
    if (!rawParam || rawParam.toLowerCase() === "all") return "All";
    const directMatch = categories.find((c) => c.toLowerCase() === rawParam.toLowerCase());
    if (directMatch) return directMatch;

    // Check if rawParam matches any category or subcategory in apiCategories
    if (Array.isArray(apiCategories) && apiCategories.length > 0) {
      const found = apiCategories.find(
        (c) =>
          (c?.name && c.name.toLowerCase() === rawParam.toLowerCase()) ||
          (c?.slug && c.slug.toLowerCase() === rawParam.toLowerCase())
      );
      if (found) {
        return formatCategoryName(found.name);
      }
    }
    return rawParam;
  }, [rawParam, categories, apiCategories]);

  const getSubcategories = (parentCategoryName) => {
    if (!Array.isArray(apiCategories) || apiCategories.length === 0 || parentCategoryName === "All") return [];

    const parentObj = apiCategories.find(
      (cat) =>
        (!cat.parent_id || cat.parent_id === 0 || cat.parent_id === "0" || cat.parent_id === "null") &&
        (formatCategoryName(cat.name).toLowerCase() === parentCategoryName.toLowerCase() ||
          cat.name.toLowerCase() === parentCategoryName.toLowerCase())
    );

    if (!parentObj) return [];

    return apiCategories
      .filter((cat) => cat.parent_id != null && Number(cat.parent_id) === Number(parentObj.id))
      .sort((a, b) => (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0) || Number(a.id) - Number(b.id));
  };

  const isCategoryActive = (c) => {
    if (c === "All") return activeCategory === "All";
    if (activeCategory.toLowerCase() === c.toLowerCase()) return true;

    // If activeCategory is a subcategory of parent category 'c'
    if (Array.isArray(apiCategories) && apiCategories.length > 0) {
      const parentObj = apiCategories.find(
        (cat) =>
          (!cat.parent_id || cat.parent_id === 0 || cat.parent_id === "0" || cat.parent_id === "null") &&
          formatCategoryName(cat.name).toLowerCase() === c.toLowerCase()
      );
      if (parentObj) {
        const activeObj = apiCategories.find(
          (cat) =>
            formatCategoryName(cat.name).toLowerCase() === activeCategory.toLowerCase() ||
            (cat.slug && cat.slug.toLowerCase() === activeCategory.toLowerCase())
        );
        if (activeObj && activeObj.parent_id && Number(activeObj.parent_id) === Number(parentObj.id)) {
          return true;
        }
      }
    }
    return false;
  };

  const filtered = useMemo(() => {
    if (!activeCategory || activeCategory.toLowerCase() === "all") {
      return products;
    }

    const targetLower = activeCategory.toLowerCase();
    const targetFormatted = formatCategoryName(activeCategory).toLowerCase();

    // 1. Find all matching categories in apiCategories (including all child subcategories if target is a parent)
    const matchingCategoryNames = new Set([targetLower, targetFormatted]);
    const matchingCategoryIds = new Set();

    if (Array.isArray(apiCategories) && apiCategories.length > 0) {
      // Find matching categories (by name, formatted name, or slug)
      const matchedCategories = apiCategories.filter((c) => {
        const cName = (c?.name || "").toLowerCase();
        const cFormatted = formatCategoryName(c?.name || "").toLowerCase();
        const cSlug = (c?.slug || "").toLowerCase();
        return cName === targetLower || cFormatted === targetFormatted || cSlug === targetLower;
      });

      matchedCategories.forEach((matched) => {
        matchingCategoryIds.add(Number(matched.id));
        matchingCategoryNames.add((matched.name || "").toLowerCase());
        matchingCategoryNames.add(formatCategoryName(matched.name || "").toLowerCase());

        // Find any child subcategories of this category (if it is a parent)
        apiCategories.forEach((sub) => {
          if (sub.parent_id != null && Number(sub.parent_id) === Number(matched.id)) {
            matchingCategoryIds.add(Number(sub.id));
            matchingCategoryNames.add((sub.name || "").toLowerCase());
            matchingCategoryNames.add(formatCategoryName(sub.name || "").toLowerCase());
          }
        });
      });
    }

    return products.filter((p) => {
      const pCatName = (p.category || "").toLowerCase();
      const pCatFormatted = formatCategoryName(p.category || "").toLowerCase();
      const pParentCatName = (p.parent_category || "").toLowerCase();
      const pParentCatFormatted = formatCategoryName(p.parent_category || "").toLowerCase();

      // Check direct category name matching
      if (matchingCategoryNames.has(pCatName) || matchingCategoryNames.has(pCatFormatted)) {
        return true;
      }

      // Check parent category name matching
      if (pParentCatName && (matchingCategoryNames.has(pParentCatName) || matchingCategoryNames.has(pParentCatFormatted))) {
        return true;
      }

      // Check category ID matching
      if (p.category_id && matchingCategoryIds.has(Number(p.category_id))) {
        return true;
      }

      if (p.parent_category_id && matchingCategoryIds.has(Number(p.parent_category_id))) {
        return true;
      }

      return false;
    });
  }, [activeCategory, products, apiCategories]);

  return (
    <div data-testid="products-page">
      <PageMeta
        title={activeCategory === "All" ? "Products" : formatCategoryName(activeCategory)}
        description="Browse our tournament-grade pickleball paddles, balls, nets, and custom equipment built for your brand."
      />
      <section className="border-b border-black/10 bg-white">
        <div className="mx-auto max-w-[1400px] px-5 pb-16 pt-16 md:pt-24 lg:px-10">
          <FadeIn>
            <div className="mb-8 flex flex-wrap items-center gap-3">
              <p className="mono-caption text-neutral-500">Catalog — {filtered.length} products</p>
              {activeCategory !== "All" && !categories.includes(activeCategory) && (
                <span className="inline-flex items-center gap-1.5 border border-brand-red/30 bg-brand-red/5 px-2.5 py-0.5 font-mono text-[11px] font-semibold text-brand-red">
                  <span>Filtered by subcategory: {activeCategory}</span>
                  <button
                    onClick={() => setParams({})}
                    className="ml-1 text-neutral-400 hover:text-brand-red"
                    title="View all products"
                  >
                    &times;
                  </button>
                </span>
              )}
            </div>
          </FadeIn>
          <h1 className="font-display text-4xl font-semibold leading-[1.02] tracking-tighter sm:text-5xl lg:text-6xl">
            <MaskedLines lines={["PRODUCTS", <span key="r" className="text-neutral-300">MADE TO ORDER.</span>]} />
          </h1>
        </div>
      </section>

      <div className="sticky top-16 z-30 border-b border-black/10 bg-white/90 backdrop-blur-xl md:top-20">
        <div className="mx-auto flex max-w-[1400px] flex-wrap items-center gap-2.5 px-5 py-4 lg:px-10" data-testid="category-filter">
          {categories.map((c) => {
            const isActive = isCategoryActive(c);
            const subs = getSubcategories(c);
            const hasSubs = subs.length > 0;

            return (
              <div key={c} className="relative group shrink-0">
                <button
                  data-testid={`category-chip-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  onClick={() => setParams(c === "All" ? {} : { category: c })}
                  className={`flex items-center gap-1.5 border px-4 py-2 font-mono text-xs font-bold uppercase tracking-[0.15em] transition-all duration-200 ${isActive
                      ? "border-brand-red bg-brand-red text-white shadow-sm"
                      : "border-black bg-black text-white hover:bg-neutral-800 hover:border-neutral-700"
                    }`}
                >
                  <span>{c}</span>
                  {hasSubs && (
                    <ChevronDown
                      size={12}
                      className="transition-transform duration-200 group-hover:rotate-180 opacity-70 group-hover:opacity-100"
                    />
                  )}
                </button>

                {/* Subcategories Dropdown on Hover */}
                {hasSubs && (
                  <div className="absolute left-0 top-full pt-1.5 hidden group-hover:block z-50 min-w-[220px] animate-in fade-in slide-in-from-top-1 duration-150">
                    <div className="bg-white border border-black/10 shadow-2xl py-1.5 divide-y divide-black/5">
                      {/* View all in this parent */}
                      <button
                        onClick={() => setParams({ category: c })}
                        className={`w-full text-left px-3.5 py-2 font-mono text-[11px] font-bold uppercase tracking-wider transition-colors flex items-center justify-between ${activeCategory.toLowerCase() === c.toLowerCase()
                            ? "bg-brand-red/10 text-brand-red"
                            : "text-brand-ink hover:bg-brand-smoke hover:text-brand-red"
                          }`}
                      >
                        <span>{c}</span>
                        <span className="font-mono text-[10px] text-neutral-400">View All</span>
                      </button>

                      {/* Individual Subcategories */}
                      <div className="py-1 max-h-72 overflow-y-auto">
                        {subs.map((sub) => {
                          const isSubSelected =
                            activeCategory.toLowerCase() === formatCategoryName(sub.name).toLowerCase() ||
                            activeCategory.toLowerCase() === (sub.name || "").toLowerCase() ||
                            (sub.slug && activeCategory.toLowerCase() === sub.slug.toLowerCase());

                          return (
                            <button
                              key={sub.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                setParams({ category: sub.name });
                              }}
                              className={`w-full text-left px-3.5 py-2 text-[12px] font-normal transition-colors flex items-center justify-between group/item ${isSubSelected
                                  ? "bg-brand-red/10 text-brand-red font-semibold"
                                  : "text-neutral-600 hover:bg-brand-smoke hover:text-brand-red"
                                }`}
                            >
                              <span className="flex items-center gap-1.5">
                                <span
                                  className={`h-1 w-1 rounded-full ${isSubSelected ? "bg-brand-red" : "bg-neutral-300 group-hover/item:bg-brand-red"
                                    }`}
                                />
                                <span>{formatCategoryName(sub.name)}</span>
                              </span>
                              {sub.count > 0 && (
                                <span className="font-mono text-[10px] text-neutral-400">
                                  ({sub.count})
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <section className="mx-auto max-w-[1400px] px-5 py-16 md:py-20 lg:px-10">
        {loading ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="aspect-[3/4] animate-pulse bg-brand-smoke" />
            ))}
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="products-grid">
            {filtered.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <Reveal>
            <p className="py-20 text-center text-neutral-400">No products in this category yet.</p>
          </Reveal>
        )}
      </section>

      <EditorialMarquee />
    </div>
  );
};

export default Products;
